import { useMemo, useState } from "react";

import Button from "components/Button";
import radixService from "api/radixService";
import { ERadixProgram } from "dto/Radix/IRadix";

import { radixErrorText } from "./radixUtils";
import styles from "./radix.module.scss";

interface Props {
  program: ERadixProgram;
  onClose: () => void;
}

const TITLE: Record<ERadixProgram, string> = {
  [ERadixProgram.WIP]: "Lista upomnień w Radix WIP",
  [ERadixProgram.PoGrun]: "Lista decyzji w Radix PoGrun",
  [ERadixProgram.GOK]: "Lista w Radix GOK",
};

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString("pl-PL") : "");

// Port RadixDocumentsListModal z Magistratu: dokumenty wprost z bazy RADIX (bez importu),
// z sygnaturą przesyłki przy tych, które już trafiły do Noty. Szukanie po adresacie,
// numerze, adresie i sygnaturze — jak SearchDescriptions listy WPF.
const RadixSourceListModal = ({ program, onClose }: Props) => {
  const { data, isLoading, error } = radixService.useGetSourceDocuments(program);
  const [search, setSearch] = useState("");

  const items = useMemo(() => {
    const all = data?.data?.Items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((d) =>
      [d.TypeDescription, d.Number, d.RegisterNumber, d.PersonLabel, d.AddressLabel, d.ParcelSignature, fmtDate(d.DecisionDate)]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [data, search]);

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={`${styles.modal} ${styles.modalWide}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>{TITLE[program]}</div>
        <div className={styles.modalBody}>
          <input className={styles.searchInput} placeholder="Szukaj…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {isLoading ? (
            <div className={styles.empty}>Odczyt bazy RADIX…</div>
          ) : error ? (
            <div className={styles.error}>{radixErrorText(error, "Nie udało się odczytać bazy RADIX.")}</div>
          ) : items.length === 0 ? (
            <div className={styles.empty}>Brak dokumentów.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Adresat</th>
                  <th>Dokument</th>
                  <th>Adres</th>
                  <th>Sygnatura przesyłki</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={`${d.Id}-${d.PersonId}`}>
                    <td>{d.PersonLabel || "—"}</td>
                    <td>
                      {d.TypeDescription} {d.Number ?? ""}
                      {d.RegisterNumber ? ` (rej. ${d.RegisterNumber})` : ""}
                    </td>
                    <td>{d.AddressLabel ?? ""}</td>
                    <td>{d.ParcelSignature ? <span className={styles.sig}>{d.ParcelSignature}</span> : ""}</td>
                    <td>{fmtDate(d.DecisionDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!isLoading && !error && (
            <div className={styles.hint}>
              Pozycji: {items.length}
              {data?.data?.Items?.length && data.data.Items.length !== items.length ? ` z ${data.data.Items.length}` : ""}
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <Button text="Zamknij" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default RadixSourceListModal;
